import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import SearchBar from '@client-shared/searchBar/searchBar.component';
import { stringifiedSpinner } from '@client-shared/spinner.component';

import { alignCenterCellStyle } from './cellStyles';
import AgGridWithCount from './agGridWithCount.component';
import {
  selectionStateSelector,
  rowSelectionStateSelector,
} from './store/selectors';
import { resetSelectionState } from './store/actions';
import { equalNodes, comparator } from './helpers';


const defaultRowHeight = 25;

const getRowHeightBasedOnContent = ({ field, margins, lineHeight, extraLength, averageCharsInLine }) => params =>
  params.data.field
    ? margins + lineHeight * (Math.ceil((params.data[field].length + extraLength) / averageCharsInLine))
    : defaultRowHeight;

const excludeFromFilteringColumnProperty = {
  getQuickFilterText() {
    return '';
  },
};

const sizeColumnsToFit = (gridApi, suppressSizeColumnsToFit) => {
  if (suppressSizeColumnsToFit) {
    return;
  }

  const eGridDiv = gridApi.getGridOption ? gridApi.getGridOption('eGridDiv') : null;
  const gridWidth = eGridDiv ? eGridDiv.offsetWidth : 0;

  if (gridWidth > 0) {
    gridApi.sizeColumnsToFit();
  } else {
    setTimeout(() => {
      const currentWidth = eGridDiv ? eGridDiv.offsetWidth : 0;
      if (currentWidth > 0) {
        gridApi.sizeColumnsToFit();
      }
    }, 100);
  }
};

export {
  getRowHeightBasedOnContent,
  excludeFromFilteringColumnProperty,
  sizeColumnsToFit,
};

class AgGrid extends PureComponent {
  static propTypes = {
    showCount: PropTypes.bool,
    withSearchBar: PropTypes.bool,
    withResetIcon: PropTypes.bool,
    onReset: PropTypes.func,
    SearchBarComponent: PropTypes.node,
    disableSearchBar: PropTypes.bool,
    componentToRenderOnSearchBar: PropTypes.node,
    messageToRenderOnSearchBar: PropTypes.node,
    defaultColDef: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    rowData: PropTypes.arrayOf(PropTypes.object), // eslint-disable-line react/forbid-prop-types
    gridSelectionState: PropTypes.shape({
      name: PropTypes.string.isRequired,
      fields: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      values: PropTypes.objectOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ]).isRequired
      ).isRequired,
    }),
    rowSelectionState: PropTypes.number,
    resetSelectionState: PropTypes.func.isRequired,
    onGridReady: PropTypes.func,
    onRowDataUpdated: PropTypes.func,
    onGridColumnsChanged: PropTypes.func,
    isRowShadingEnabled: PropTypes.bool,
    allowRowShadowing: PropTypes.bool,
    isGridLoading: PropTypes.bool,
    suppressSizeColumnsToFit: PropTypes.bool.isRequired,
    isScrollDisabled: PropTypes.bool,
    autoSelectFirstRowOnFirstLoad: PropTypes.bool,
    absoluteScroll: PropTypes.bool,
    headerHeight: PropTypes.number,
    rowHeight: PropTypes.number,
    columnDefs: PropTypes.arrayOf(PropTypes.object).isRequired, // eslint-disable-line react/forbid-prop-types
    createCustomContextMenu: PropTypes.func,
    initialQuickFilterValue: PropTypes.string,
    rowBuffer: PropTypes.number,
    withScrollPagination: PropTypes.bool,
    onViewportChanged: PropTypes.func,
    onFilterChanged: PropTypes.func,
  };

  static defaultProps = {
    headerHeight: 40,
    rowHeight: defaultRowHeight,
    onRowDataUpdated: null,
    onGridColumnsChanged: null,
    rowSelection: 'single',
    showCount: true,
    suppressCellFocus: true,
    withSearchBar: false,
    withResetIcon: false,
    SearchBarComponent: null,
    disableSearchBar: false,
    allowRowShadowing: false,
    componentToRenderOnSearchBar: null,
    messageToRenderOnSearchBar: null,
    suppressSizeColumnsToFit: false,
    isScrollDisabled: false,
    autoSelectFirstRowOnFirstLoad: false,
    isGridLoading: false,
    absoluteScroll: true,
    isRowShadingEnabled: false,
    initialQuickFilterValue: '',
    withScrollPagination: false,
    onViewportChanged: null,
    onFilterChanged: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      filter: props.initialQuickFilterValue,
      dataAlreadyLoadedOnce: false,
      firstRow: 0,
      lastRow: 0,
      rowCount: 0,
    };
  }

  componentDidUpdate = prevProps => {
    if (this.gridApi) {
      if (prevProps.columnDefs !== this.props.columnDefs) {
        sizeColumnsToFit(this.gridApi, this.props.suppressSizeColumnsToFit);
      }

      if (prevProps.isGridLoading !== this.props.isGridLoading) {
        if (this.props.isGridLoading) {
          this.gridApi?.showLoadingOverlay();
        } else if (this.props.rowData?.length === 0) {
          this.gridApi.showNoRowsOverlay();
        } else {
          this.gridApi.hideOverlay();
        }
      }
    }
  };

  componentWillUnmount = () => {
    this.props.resetSelectionState();
    if (this.gridApi) {
      this.gridApi.resetColumnState();
    }
  };

  componentSizeColumnsToFit = params => {
    const { suppressSizeColumnsToFit } = this.props;
    sizeColumnsToFit(params.api, suppressSizeColumnsToFit);
  };

  refreshScrollPagination = ({ api }) => {
    if (this.props.withScrollPagination) {
      const firstRow = api.getFirstDisplayedRowIndex();
      const lastRow = api.getLastDisplayedRowIndex();
      this.setState({
        firstRow: lastRow < 0 ? 0 : firstRow + 1,
        lastRow: lastRow < 0 ? 0 : lastRow + 1,
        rowCount: api?.getDisplayedRowCount(),
      });
    }
  };

  onGridReady = params => {
    const { gridSelectionState, resetSelectionState, suppressSizeColumnsToFit } = this.props;
    sizeColumnsToFit(params.api, suppressSizeColumnsToFit);

    if (this.props.isGridLoading && this.gridApi) {
      this.gridApi.showLoadingOverlay();
    }

    if (gridSelectionState && this.gridApi) {
      this.gridApi.forEachNode(node => {
        if (equalNodes(node, gridSelectionState)) {
          node.setSelected(true, true);
          this.gridApi.ensureNodeVisible(node, 'top');
          resetSelectionState();
        }
      });
    }

    if (this.props.onGridReady) {
      this.props.onGridReady(params);
    }

    if (this.props.absoluteScroll) {
      this.selectRowDebounced = debounce(() => {
        const { rowIndex } = this.gridApi.getFocusedCell();
        this.gridApi.getDisplayedRowAtIndex(rowIndex).setSelected(true);
      }, 300);
    }
  };

  onRowDataUpdated = params => {
    this.gridApi = params.api;
    this.manageSelectionState(params);
    this.selectFirstRow(params.api);

    if (this.props.isGridLoading) {
      params.api.showLoadingOverlay();
    }

    if (this.props.onRowDataUpdated) {
      this.props.onRowDataUpdated(params);
    }

    this.refreshScrollPagination(params);
  };

  onGridColumnsChanged = params => {
    if (this.props.isGridLoading) {
      params.api.showLoadingOverlay();
    }

    if (this.props.onGridColumnsChanged) {
      this.props.onGridColumnsChanged(params);
    }
  };

  onViewportChanged = params => {
    if (this.props.isGridLoading) {
      params.api.showLoadingOverlay();
    }


    if (this.props.onViewportChanged) {
      this.props.onViewportChanged(params);
    }

    this.refreshScrollPagination(params);
  };

  onFilterChanged = params => {
    if (this.props.isGridLoading) {
      params.api.showLoadingOverlay();
    }

    if (this.props.onFilterChanged) {
      this.props.onFilterChanged(params);
    }

    this.refreshScrollPagination(params);
  };

  defaultColumnDefs = {
    menuTabs: ['filterMenuTab', 'generalMenuTab', 'columnsMenuTab'],
    comparator,
    sortable: true,
    filter: true,
    resizable: true,
    ...alignCenterCellStyle,
    ...this.props.defaultColDef,
  };

  getMainMenuItems = params => {
    const mainMenuItemsWithoutToolPanel = params.defaultItems.slice(0, -1);
    return mainMenuItemsWithoutToolPanel;
  };

  reset = () => {
    this.gridApi.setFilterModel(null);
    this.gridApi.resetColumnState();

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  setFilter = filter => {
    this.setState({ filter });
  };

  unsetFilter = () => {
    this.setState({ filter: '' });
  };

  manageSelectionState = ({ api }) => {
    const {
      gridSelectionState,
      rowSelectionState,
      resetSelectionState,
      suppressSizeColumnsToFit,
    } = this.props;

    sizeColumnsToFit(api, suppressSizeColumnsToFit);

    if (rowSelectionState && this.gridApi) {
      this.gridApi.forEachNode(node => {
        if (node.rowIndex === rowSelectionState) {
          node.setSelected(true, true);
          this.gridApi.ensureNodeVisible(node, 'top');
          resetSelectionState();
        }
      });
      return;
    }

    if (gridSelectionState && this.gridApi) {
      this.gridApi.forEachNode(node => {
        if (equalNodes(node, gridSelectionState)) {
          node.setSelected(true, true);
          this.gridApi.ensureNodeVisible(node, 'top');
          resetSelectionState();
        }
      });
    }
  };

  selectFirstRow = api => {
    if (this.props.autoSelectFirstRowOnFirstLoad) {
      if (this.props.rowData.length === 0 || this.state.dataAlreadyLoadedOnce) {
        return;
      }

      this.setState({ dataAlreadyLoadedOnce: true });

      api.forEachNode(node => {
        if (node.childIndex === 0) {
          node.setSelected(true, true);
          api.ensureNodeVisible(node, 'top');
          this.props.resetSelectionState();
        }
      });
    }
  };

  createCustomContextMenu = params => this.props.createCustomContextMenu
    ? this.props.createCustomContextMenu(params)
    : [
      'copy',
      'copyWithHeaders',
      'separator',
      {
        name: 'Export',
        subMenu: [
          'csvExport',
          {
            name: 'Excel Export',
            action: () => this.gridApi.exportDataAsExcel(),
          },
        ],
      },
    ];

  handleKeypress = event => {
    if (this.props.absoluteScroll) {
      const { gridApi } = this;
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        this.selectRowDebounced();
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const visibleColumns = gridApi.getAllDisplayedVirtualColumns().filter(column => !column.pinned);
        if (visibleColumns.length === 0) {
          return;
        }

        const leftmostVisibleColumn = visibleColumns[0];
        const rightmostVisibleColumn = visibleColumns[visibleColumns.length - 1];

        if (event.key === 'ArrowRight') {
          const invisibleColumnOnTheRight = gridApi.getDisplayedColAfter(rightmostVisibleColumn);
          if (invisibleColumnOnTheRight && !invisibleColumnOnTheRight.pinned) {
            gridApi.ensureColumnVisible(invisibleColumnOnTheRight.colId);
          }
        } else {
          const invisibleColumnOnTheLeft = gridApi.getDisplayedColBefore(leftmostVisibleColumn);
          if (invisibleColumnOnTheLeft && !invisibleColumnOnTheLeft.pinned) {
            gridApi.ensureColumnVisible(invisibleColumnOnTheLeft.colId);
          }
        }
      }
    }
  };

  render() {
    const {
      componentToRenderOnSearchBar,
      messageToRenderOnSearchBar,
      withSearchBar,
      withResetIcon,
      SearchBarComponent,
      disableSearchBar,
      showCount,
      allowRowShadowing,
      isRowShadingEnabled,
      isScrollDisabled,
      rowData,
      withScrollPagination,
      ...gridProps
    } = this.props;
    const { filter } = this.state;

    const searchBarProps = {
      onCancel: this.unsetFilter,
      onSearch: this.setFilter,
      value: filter,
      withResetIcon,
      reset: this.reset,
    };

    return (
      <div className="ag-grid-custom">
        {!withSearchBar
          ? null
          : (
            <div className="row">
              {!componentToRenderOnSearchBar
                ? null
                : (
                  <div className="col-3">
                    {componentToRenderOnSearchBar}
                  </div>
                )
              }

              {!messageToRenderOnSearchBar
                ? null
                : (
                  <div className="col-6 ml-auto">
                    {messageToRenderOnSearchBar}
                  </div>
                )
              }

              {
                SearchBarComponent ? (
                  <SearchBar
                    Component={SearchBarComponent}
                    {...searchBarProps}
                  />
                ) : (
                  <div
                    className={classnames({
                      'col-3': Boolean(messageToRenderOnSearchBar),
                      'col-5': !messageToRenderOnSearchBar,
                      'ml-auto': !messageToRenderOnSearchBar,
                    })}
                  >
                    <SearchBar
                      {...searchBarProps}
                      disabled={disableSearchBar}
                    />
                  </div>
                )
              }
            </div>
          )
        }
        <div
          onKeyDown={this.handleKeypress}
          tabIndex="0"
          className={classnames(
            'ag-theme-bootstrap',
            { 'ag-theme-bootstrap-shadowed': allowRowShadowing && isRowShadingEnabled },
            { 'ag-grid-disable-scroll': isScrollDisabled },
            { 'ag-grid-with-scroll-pagination': withScrollPagination }
          )}
        >
          {
            showCount ? (
              <AgGridWithCount
                quickFilterText={filter}
                rowMultiSelectWithClick
                suppressDragLeaveHidesColumns
                columnMenu="legacy"
                {...gridProps}
                defaultColDef={this.defaultColumnDefs}
                getMainMenuItems={this.getMainMenuItems}
                getContextMenuItems={this.createCustomContextMenu}
                onGridReady={this.onGridReady}
                onRowDataUpdated={this.onRowDataUpdated}
                onGridColumnsChanged={this.onGridColumnsChanged}
                onViewportChanged={this.onViewportChanged}
                onFilterChanged={this.onFilterChanged}
                overlayLoadingTemplate={stringifiedSpinner}
                rowData={rowData}
                // if show withScrollPagination, it should set row buffer as 0, or the number of rows will be added to the value of rowBuffer.
                rowBuffer={withScrollPagination ? 0 : gridProps.rowBuffer}
              />
            ) : (
              <AgGridReact
                quickFilterText={filter}
                rowMultiSelectWithClick
                suppressDragLeaveHidesColumns
                columnMenu="legacy"
                {...gridProps}
                defaultColDef={this.defaultColumnDefs}
                getContextMenuItems={this.createCustomContextMenu}
                getMainMenuItems={this.getMainMenuItems}
                onGridReady={this.onGridReady}
                onRowDataUpdated={this.onRowDataUpdated}
                onGridColumnsChanged={this.onGridColumnsChanged}
                onViewportChanged={this.onViewportChanged}
                onFilterChanged={this.onFilterChanged}
                overlayLoadingTemplate={stringifiedSpinner}
                rowData={rowData}
                // if show withScrollPagination, it should set row buffer as 0, or the number of rows will be added to the value of rowBuffer.
                rowBuffer={withScrollPagination ? 0 : gridProps.rowBuffer}
              />
            )
          }
          {
            withScrollPagination && <p className="scroll-pagination">Rows: <b>{this.state.firstRow}</b>-<b>{this.state.lastRow}</b> of <b>{this.state.rowCount}</b></p>
          }
        </div>
      </div>
    );
  }
}

export default connect(state => ({
  gridSelectionState: selectionStateSelector(state),
  rowSelectionState: rowSelectionStateSelector(state),
}), {
  resetSelectionState,
})(AgGrid);
